import 'css/src/components/common/ui/TextInput.module.scss';

import React from 'react';
import classNames from 'classnames';

import { useIsOnScreen } from '../hooks/useIsOnScreen';

export enum TEXT_INPUT_VARIANTS {
    DEFAULT = 'default',
    STANDARD = 'standard',
    UNDERLINED = 'underlined',
    TEXTAREA = 'textarea',
}

export enum FEEDBACK_MESSAGE_TYPES {
    INFO = 'info',
    WARNING = 'warning',
    ERROR = 'error',
}

const DEFAULT_MAX_LENGTH = 524288;

/**
 * These are suggested values from:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token-list
 * but autocomplete field theoretically accepts any str
 */
export enum AUTOCOMPLETE_VALUES {
    OFF = 'off',
    ON = 'on',
    NAME = 'name',
    FIRST_NAME = 'given-name', // there's no actual "first-name"
    LAST_NAME = 'family-name',
    EMAIL = 'email',
    USERNAME = 'username',
    NEW_PASSWORD = 'new-password',
    CURRENT_PASSWORD = 'current-password',
    OTP = 'one-time-code',
    BILLING_ADDRESS = 'billing street-address',
}

type AUTOCOMPLETE_TYPE = AUTOCOMPLETE_VALUES | string;

type StandardTextInputProps = Omit<TextInputProps, 'variant'> & {
    withContainer?: boolean;
    multiline?: boolean;
    ignorePasswordManagers?: boolean;
    ref?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
};
const StandardTextInput = React.memo(function StandardTextInput({
    text,
    labelText,
    type = 'text',
    'data-testid': dataTestId,
    onSubmit,
    id,
    name,
    className = '',
    autoFocus = false,
    checkCharLimit = false,
    selectOnFocus = false,
    charLimit = undefined,
    minLength = 0,
    required = false,
    trackCharLimitEvent = undefined,
    dynamicInputBoxSize = false,
    formFeedbackMessage = undefined,
    formFeedbackMessageType = FEEDBACK_MESSAGE_TYPES.ERROR,
    placeholder = '',
    onChange,
    onFocus,
    onBlur,
    onKeyUp,
    onKeyDown,
    onEnterSubmit,
    withContainer = true,
    submitOnBlur = false,
    disabled = false,
    iconLeft,
    iconRight,
    showClear = false,
    onClear,
    readOnly = false,
    'data-bs-toggle': dataBSToggle = '',
    multiline = false,
    lines = 3,
    autocomplete = AUTOCOMPLETE_VALUES.OFF,
    ignorePasswordManagers = true,
    dir = 'auto',
    resize,
    ref,
}: StandardTextInputProps) {
    const [textValue, setTextValue] = React.useState(text);
    const [feedbackMessage, setFeedbackMessage] = React.useState(formFeedbackMessage);
    const [hasError, setHasError] = React.useState(false);
    const [exceededCharacterLimit, setExceededCharacterLimit] = React.useState(false);

    const _ref = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const inputRef = ref || _ref;
    const isRefObject = !!inputRef && 'current' in inputRef;
    const hasAutoFocused = React.useRef(false);

    const isOnScreen = useIsOnScreen(inputRef);

    // the autoFocus attribute is flaky in cases of conditional rendering, so we are firing focus manually when the element is visible on screen
    // Only auto-focus once to prevent stealing focus from other inputs (e.g., in modals)
    if (autoFocus && isRefObject && isOnScreen && !hasAutoFocused.current) {
        inputRef.current?.focus();
        hasAutoFocused.current = true;
    }

    const enforceCharLimit = React.useCallback(
        (textNumCharacters: number) => {
            if (!checkCharLimit) {
                return;
            }
            if (charLimit === undefined) {
                throw new Error('charLimit must be defined when checkCharLimit is true');
            }

            let sendTrackingEvent = false;

            if (textNumCharacters >= charLimit) {
                // Only send tracking event when the title first exceeds the character limit
                if (!exceededCharacterLimit) {
                    sendTrackingEvent = true;
                }

                setFeedbackMessage(`Maximum ${charLimit} characters`);
                setHasError(true);

                if (sendTrackingEvent && typeof trackCharLimitEvent === 'function') {
                    trackCharLimitEvent(textNumCharacters);
                    setExceededCharacterLimit(true);
                }
            } else {
                setFeedbackMessage('');
                setHasError(false);
                setExceededCharacterLimit(false);
            }
        },
        [charLimit, checkCharLimit, exceededCharacterLimit, trackCharLimitEvent],
    );

    const handleChange = React.useCallback(
        (e: TypeInputChangeEvent) => {
            setTextValue(e.target.value);
            enforceCharLimit(e.target.value.length);
            typeof onChange === 'function' && onChange(e);
        },
        [onChange, enforceCharLimit],
    );

    const handleFocus = React.useCallback(
        (e: TypeInputFocusEvent) => {
            if (selectOnFocus) {
                e.currentTarget.select();
            }
            typeof onFocus === 'function' && onFocus(e);
        },
        [onFocus, selectOnFocus],
    );

    const handleBlur = React.useCallback(
        (e: TypeInputFocusEvent) => {
            typeof onBlur === 'function' && onBlur(e);

            if (submitOnBlur) {
                typeof onSubmit === 'function' && onSubmit(e.target.value);
            }
        },
        [onBlur, onSubmit, submitOnBlur],
    );

    const handleKeyUp = React.useCallback(
        (e: TypeInputKeyboardEvent) => {
            typeof onKeyUp === 'function' && onKeyUp(e);
        },
        [onKeyUp],
    );

    const handleKeyDown = React.useCallback(
        (e: TypeInputKeyboardEvent) => {
            typeof onKeyDown === 'function' && onKeyDown(e);

            if (
                typeof onEnterSubmit === 'function' &&
                !e.defaultPrevented &&
                e.key === 'Enter' &&
                !e.shiftKey &&
                !e.ctrlKey &&
                !e.altKey &&
                !e.metaKey
            ) {
                const value = e.currentTarget.value;
                if (value.trim()) {
                    e.preventDefault();
                    onEnterSubmit(value);
                }
            }
        },
        [onKeyDown, onEnterSubmit],
    );

    const handleClear = React.useCallback(
        (e: TypeInputMouseEvent) => {
            setTextValue('');
            typeof onClear === 'function' && onClear(e);
        },
        [onClear],
    );

    const styleProp: React.CSSProperties = {};
    if (dynamicInputBoxSize) {
        styleProp.width = `${text.length}ch`;
    }
    if (resize) {
        styleProp.resize = resize;
    }

    // controlled input
    React.useEffect(() => {
        setTextValue(text);
    }, [text]);

    React.useEffect(() => {
        setFeedbackMessage(formFeedbackMessage);

        if (formFeedbackMessage && formFeedbackMessageType === FEEDBACK_MESSAGE_TYPES.ERROR) {
            setHasError(true);
        } else {
            setHasError(false);
        }
    }, [formFeedbackMessage, formFeedbackMessageType]);

    let iconLeftNode = null;
    if (iconLeft) {
        if (typeof iconLeft === 'string' && iconLeft !== '') {
            iconLeftNode = (
                <span className="material-symbols-outlined text-input-icon-left">{iconLeft}</span>
            );

            // there is no better icon than this SVG and this has been around for a long time
            // we'll make this exception case just for this highly visible icon
            if (iconLeft === 'blog_link') {
                iconLeftNode = (
                    <img
                        className="blog-link-svg text-input-icon-left"
                        src="https://storage.googleapis.com/lumen5-prod-images/blog-link.svg"
                        alt="blog link"
                    />
                );
            }
        } else if (React.isValidElement(iconLeft)) {
            iconLeftNode = <span className="text-input-icon-left">{iconLeft}</span>;
        }
    }
    let iconRightNode = null;
    if (iconRight) {
        if (typeof iconRight === 'string' && iconRight !== '') {
            iconRightNode = (
                <span className="material-symbols-outlined text-input-icon-right">{iconRight}</span>
            );
        } else if (React.isValidElement(iconRight)) {
            iconRightNode = <span className="text-input-icon-right">{iconRight}</span>;
        }
    }
    let clearIconNode = null;
    if (textValue && textValue.trim() !== '' && showClear) {
        clearIconNode = (
            <span
                className="material-symbols-outlined text-input-clear-icon material-symbols-f1"
                onClick={handleClear}
            >
                cancel
            </span>
        );
    }

    const conditionallyRenderIdProp = id ? { id } : {};
    const conditionallyRenderNameProp = name ? { name } : {};

    const commonProps = {
        // common props
        ...conditionallyRenderIdProp,
        ...conditionallyRenderNameProp,
        style: styleProp,
        className: classNames('base-input', className, {
            error: hasError,
            'text-input-bordered': withContainer,
            multiline: multiline,
        }),
        'data-testid': dataTestId,
        'data-1p-ignore': ignorePasswordManagers,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyUp: handleKeyUp,
        onKeyDown: handleKeyDown,
        value: textValue,
        maxLength: checkCharLimit === true ? charLimit : DEFAULT_MAX_LENGTH,
        placeholder,
        minLength,
        required,
        disabled,
        'data-bs-toggle': dataBSToggle,
        readOnly,
        autoComplete: autocomplete,
        dir,
    };

    let inputNode = null;
    if (multiline) {
        inputNode = (
            <textarea
                {...commonProps}
                rows={lines}
                ref={inputRef as React.Ref<HTMLTextAreaElement>}
            />
        );
    } else {
        inputNode = (
            <input
                {...commonProps}
                type={type}
                role="input"
                ref={inputRef as React.Ref<HTMLInputElement>}
            />
        );
    }

    const renderFeedbackMessage = () => {
        if (!feedbackMessage) {
            return null;
        } else if (typeof feedbackMessage === 'string') {
            return (
                <span
                    className={`feedback-text ${formFeedbackMessageType}`}
                    dangerouslySetInnerHTML={{ __html: feedbackMessage }}
                ></span>
            );
        }
        return (
            <span className={`feedback-text ${formFeedbackMessageType}`}>{feedbackMessage}</span>
        );
    };

    return (
        <>
            {labelText && <label className="mb-l5-4">{labelText}</label>}
            <div
                className={classNames('input-container', {
                    'with-left-icon': !!iconLeftNode,
                    'with-right-icon': !!iconRightNode,
                    'with-clear-icon': !!clearIconNode,
                })}
            >
                {iconLeftNode}
                {inputNode}
                {clearIconNode}
                {iconRightNode}
            </div>
            {renderFeedbackMessage()}
        </>
    );
});

/**
 * Stateful <input/> that can check and limit characters. UI changes depending on
 * whether the <input/> is editing or not.
 *
 * @param {string} text - Text of this component
 * @param {func} onSubmit - callback function when input is changed onBlur or user presses enter
 * @param {string} className - customizable classname. You should pass any bootstrap utility classes to format the <input/> the way you want
 * @param {bool} checkCharLimit - determines if component should check for character limit, which is set by another prop
 * @param {number} charLimit - used when checkCharLimit == true. Limits the text on the input, exceeding characters are ignored
 * @param {func} trackCharLimitEvent - used for analytics tracking, fired when input exceeds character limit
 * @param {bool} dynamicInputBoxSize - If true, sets the width of the input to be width of the text
 * @param {bool} showIconOnHover - If false, doesn't show the pencil (edit) icon on hover
 */

type UnderlinedTextInputProps = Omit<TextInputProps, 'variant' | 'disabled'> & {
    showIconOnHover?: boolean;
    // Start in edit mode (focused) on mount instead of the read-only display
    // with the pencil affordance. Useful for a freshly-added list item.
    autoEdit?: boolean;
};
const UnderlinedTextInput = React.memo<UnderlinedTextInputProps>(function UnderlinedInput({
    text,
    type,
    onSubmit,
    onChange,
    onBlur,
    id = '',
    className = '',
    checkCharLimit = false,
    charLimit = undefined,
    trackCharLimitEvent = undefined,
    dynamicInputBoxSize = false,
    submitOnBlur,
    showIconOnHover = true,
    readOnly = false,
    autocomplete = AUTOCOMPLETE_VALUES.OFF,
    autoEdit = false,
}) {
    const [editing, setEditing] = React.useState(autoEdit);
    const [controlledText, setControlledText] = React.useState(text);

    const startEditing = React.useCallback(() => {
        setEditing(true);
    }, []);

    const stopEditing = React.useCallback(() => {
        setEditing(false);
    }, []);

    const handleSubmit = React.useCallback(() => {
        if (!onSubmit || typeof onSubmit !== 'function') {
            stopEditing();
            return;
        }

        onSubmit(controlledText);
        stopEditing();
    }, [onSubmit, stopEditing, controlledText]);

    const onClick = React.useCallback(() => {
        startEditing();
    }, [startEditing]);

    const handleOnBlur = React.useCallback(
        (e: TypeInputFocusEvent) => {
            e.preventDefault();
            stopEditing();

            typeof onBlur === 'function' && onBlur(e);
            if (submitOnBlur) {
                handleSubmit();
            }
        },
        [onBlur, submitOnBlur, handleSubmit, stopEditing],
    );

    const handleOnChange = React.useCallback(
        (e: TypeInputChangeEvent) => {
            typeof onChange === 'function' && onChange(e);
            setControlledText(e.target.value);
        },
        [onChange],
    );

    const onSubmitCallback = React.useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            // submit from the form
            handleSubmit();
        },
        [handleSubmit],
    );

    const onFocus = React.useCallback((e: TypeInputFocusEvent) => {
        e.target.select();
    }, []);

    const stopPropagation = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    React.useEffect(() => {
        setControlledText(text);
    }, [text]);

    const editingMode = (
        <form
            onSubmit={onSubmitCallback}
            className={classNames(
                className,
                'text-input-underlined-input-form editing display-mode-container',
            )}
            onClick={stopPropagation}
        >
            <StandardTextInput
                text={text}
                onFocus={onFocus}
                onBlur={handleOnBlur}
                checkCharLimit={checkCharLimit}
                charLimit={charLimit}
                onChange={handleOnChange}
                trackCharLimitEvent={trackCharLimitEvent}
                dynamicInputBoxSize={dynamicInputBoxSize}
                withContainer={false}
                type={type}
                id={id}
                submitOnBlur={false} // this component handles submitOnBlur
                readOnly={readOnly}
                autocomplete={autocomplete}
                autoFocus={true}
            />
        </form>
    );

    const displayMode = (
        // The standard text input component while editing is inbuilt with roughly 12px spacing at the end
        // We add the equivalent in 'pe-l5-12' here to make up for that so that the input width between editing and non-editing is roughly the same
        <div className="position-relative display-mode-container" onClick={onClick}>
            <div className={classNames(className, 'text-input-underlined pe-l5-8')}>
                {text}&nbsp;
            </div>
            {showIconOnHover && <i className="material-symbols-outlined">edit</i>}
        </div>
    );

    return <div className="title-editor">{editing ? editingMode : displayMode}</div>;
});

export type TypeInputChangeEvent =
    | React.ChangeEvent<HTMLInputElement>
    | React.ChangeEvent<HTMLTextAreaElement>;
export type TypeInputFocusEvent =
    | React.FocusEvent<HTMLInputElement>
    | React.FocusEvent<HTMLTextAreaElement>;
export type TypeInputClipboardEvent =
    | React.ClipboardEvent<HTMLInputElement>
    | React.ClipboardEvent<HTMLTextAreaElement>;
export type TypeInputKeyboardEvent =
    | React.KeyboardEvent<HTMLInputElement>
    | React.KeyboardEvent<HTMLTextAreaElement>;
export type TypeInputMouseEvent =
    | React.MouseEvent<HTMLInputElement>
    | React.MouseEvent<HTMLTextAreaElement>;

type TextInputProps = {
    variant?: TEXT_INPUT_VARIANTS;
    text: string;
    labelText?: React.ReactNode;
    type?: 'text' | 'password' | 'email' | 'number';
    name?: string;
    ['data-testid']?: string;
    id?: string;
    className?: string;
    autoFocus?: boolean;
    checkCharLimit?: boolean;
    // if true, selects all contents when focusing on the input
    selectOnFocus?: boolean;
    charLimit?: number;
    showClear?: boolean;
    required?: boolean;
    minLength?: number;
    trackCharLimitEvent?: (textNumCharacters: number) => void;
    dynamicInputBoxSize?: boolean;
    formFeedbackMessage?: string | React.JSX.Element;
    formFeedbackMessageType?: FEEDBACK_MESSAGE_TYPES;
    styleProp?: React.CSSProperties;
    placeholder?: string;
    submitOnBlur?: boolean;
    disabled?: boolean;
    iconLeft?: string | React.ReactNode;
    iconRight?: string | React.ReactNode;
    onSubmit?: (text: string) => void | Promise<unknown>;
    onChange?: (e: TypeInputChangeEvent) => void;
    onFocus?: (e: TypeInputFocusEvent) => void;
    onBlur?: (e: TypeInputFocusEvent) => void;
    onKeyUp?: (e: TypeInputKeyboardEvent) => void;
    onKeyDown?: (e: TypeInputKeyboardEvent) => void;
    /**
     * Fires when the user presses plain Enter on a non-empty input. Skipped when
     * any modifier (Shift/Ctrl/Alt/Meta) is held, when the trimmed value is empty,
     * or when an `onKeyDown` consumer has already called `preventDefault()`.
     * Useful for replacing hand-rolled submit-on-Enter handlers.
     */
    onEnterSubmit?: (text: string) => void;
    onClear?: (e: TypeInputMouseEvent) => void;
    'data-bs-toggle'?: string;
    readOnly?: boolean;
    lines?: number;
    autocomplete?: AUTOCOMPLETE_TYPE;
    ignorePasswordManagers?: boolean;
    dir?: 'auto' | 'ltr' | 'rtl';
    resize?: 'vertical' | 'horizontal' | 'both';
};
const TextInput = React.memo(function TextInput({
    variant,
    disabled,
    lines,
    type = 'text',
    ref,
    ...inputProps
}: TextInputProps & StandardTextInputProps & UnderlinedTextInputProps) {
    let inputNode;

    switch (variant) {
        case TEXT_INPUT_VARIANTS.UNDERLINED: {
            if (lines) {
                throw new Error(
                    'UnderlinedTextInput does not support lines prop. Use `DEFAULT|STANDARD` variant instead.',
                );
            }
            if (disabled !== undefined) {
                throw new Error(
                    'UnderlinedTextInput does not support disabled prop. Use `DEFAULT|STANDARD` variant instead.',
                );
            }
            const componentProps = {
                ...inputProps,
                type,
            } as UnderlinedTextInputProps;
            inputNode = <UnderlinedTextInput {...componentProps} />;
            break;
        }
        case TEXT_INPUT_VARIANTS.TEXTAREA: {
            const componentProps = {
                ...inputProps,
                multiline: true,
                lines,
                disabled,
            } as StandardTextInputProps;
            inputNode = <StandardTextInput {...componentProps} ref={ref} />;
            break;
        }
        case TEXT_INPUT_VARIANTS.STANDARD:
        default: {
            if (lines) {
                throw new Error(
                    'StandardTextInput does not support lines prop. Use `TEXTAREA` variant instead.',
                );
            }
            const componentProps = {
                ...inputProps,
                type,
                disabled,
            } as StandardTextInputProps;
            inputNode = <StandardTextInput {...componentProps} ref={ref} />;
            break;
        }
    }

    return <>{inputNode}</>;
});

export default TextInput;
